import React from 'react'

import {
  HiMiniChevronDown,
  HiOutlineCog6Tooth,
  HiOutlineInformationCircle,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import Popover from '@/components/common/PopoverV2'
import Switch from '@/components/common/Switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

export type Settings = {
  autoSubmit: boolean
  useFullTextSearch: boolean
}

interface SettingsProps {
  settings: Settings
  setSettings: (settings: Settings) => void
  className?: string
}

const Settings: React.FC<SettingsProps> = ({
  settings,
  setSettings,
  className,
}) => {
  const toggleAutoSubmit = () => {
    setSettings({ ...settings, autoSubmit: !settings.autoSubmit })
  }

  const toggleUseFullTextSearch = () => {
    setSettings({
      ...settings,
      useFullTextSearch: !settings.useFullTextSearch,
    })
  }

  const handleOpenAutoFocus = (e: Event) => {
    e.preventDefault()
  }

  return (
    <Popover>
      <Popover.Trigger asChild={true}>
        <Button variant="outline" className={twMerge('gap-x-1', className)}>
          <HiOutlineCog6Tooth className="w-[18px] h-[18px] text-primary-700 dark:text-primary-300" />
          <HiMiniChevronDown className="w-4 h-4 text-primary-500 dark:text-primary-400" />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        className="border border-primary-300 dark:border-primary-800 max-w-72"
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <div className="flex flex-col gap-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-x-1">
              <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                Auto-submit
              </span>
              <Tooltip delayDuration={250}>
                <TooltipTrigger>
                  <HiOutlineInformationCircle className="w-4 h-4 text-primary-500 dark:text-primary-400" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px]">
                  Automatically submit a search request anytime you select,
                  change, or remove a filter.
                </TooltipContent>
              </Tooltip>
            </div>
            <Switch checked={settings.autoSubmit} onClick={toggleAutoSubmit} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-x-1">
              <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                Keyword search only
              </span>
              <Tooltip delayDuration={250}>
                <TooltipTrigger>
                  <HiOutlineInformationCircle className="w-4 h-4 text-primary-500 dark:text-primary-400" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px]">
                  Only match your search query on keywords found in the
                  opportunity. This is useful if you know exactly what
                  you&apos;re looking for.
                </TooltipContent>
              </Tooltip>
            </div>
            <Switch
              checked={settings.useFullTextSearch}
              onClick={toggleUseFullTextSearch}
            />
          </div>
        </div>
      </Popover.Content>
    </Popover>
  )
}

export default Settings
